import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
  Activity, LayoutDashboard, Trophy, Users, UserSquare2,
  MapPin, CalendarDays, LogOut, Menu, X, BarChart3, Crown,
  UserCog, Settings, Globe,
} from "lucide-react";

function LanguageToggle({ variant = "desktop" }) {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage || i18n.language || "pt-BR").toLowerCase().startsWith("en") ? "en" : "pt-BR";
  const change = (lng) => { i18n.changeLanguage(lng); };
  const base = variant === "desktop"
    ? "inline-flex items-center gap-1.5 border border-navy-border rounded-sm text-xs font-heading uppercase tracking-widest overflow-hidden"
    : "inline-flex items-center gap-1.5 border border-navy-border rounded-sm text-xs font-heading uppercase tracking-widest overflow-hidden";
  return (
    <div className={base} data-testid="lang-toggle">
      <button
        type="button"
        data-testid="lang-toggle-pt"
        onClick={() => change("pt-BR")}
        className={`px-2 py-1.5 transition-colors ${current === "pt-BR" ? "bg-orange text-white" : "text-ink-secondary hover:text-white"}`}
      >
        PT
      </button>
      <button
        type="button"
        data-testid="lang-toggle-en"
        onClick={() => change("en")}
        className={`px-2 py-1.5 transition-colors ${current === "en" ? "bg-orange text-white" : "text-ink-secondary hover:text-white"}`}
      >
        EN
      </button>
    </div>
  );
}

export function GpsLogo({ className = "" }) {
  return (
    <Link to="/" className={`flex items-center group ${className}`} data-testid="gps-logo">
      <img
        src="/brand/gps-logo.png"
        alt="GPS STATS"
        className="h-10 md:h-12 w-auto object-contain select-none pointer-events-none"
        draggable={false}
      />
    </Link>
  );
}

export function PublicLayout() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.get("/site/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const navLink = ({ isActive }) =>
    `font-heading uppercase tracking-widest text-sm px-3 py-2 transition-colors ${
      isActive ? "text-orange" : "text-ink-secondary hover:text-white"
    }`;
  const mobileNavLink = ({ isActive }) =>
    `block font-heading uppercase tracking-widest text-base px-4 py-3 border-b border-navy-border ${
      isActive ? "text-orange bg-navy-surface/40" : "text-ink-secondary hover:text-white hover:bg-navy-surface/40"
    }`;

  return (
    <div className="min-h-screen bg-navy text-ink-primary font-body">
      <header className="border-b border-navy-border bg-navy-deep/95 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8 py-3 flex items-center justify-between gap-3">
          <GpsLogo />
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" end className={navLink} data-testid="nav-home">{t("nav.home")}</NavLink>
            <NavLink to="/games" className={navLink} data-testid="nav-games">{t("nav.games")}</NavLink>
            <NavLink to="/teams" className={navLink} data-testid="nav-teams">{t("nav.teams")}</NavLink>
            <NavLink to="/players" className={navLink} data-testid="nav-players">{t("nav.players")}</NavLink>
            <NavLink to="/leaders" className={navLink} data-testid="nav-leaders">{t("nav.leaders")}</NavLink>
          </nav>
          <div className="flex items-center gap-2">
            <div className="hidden sm:block"><LanguageToggle /></div>
            {user && user !== false ? (
              <Link to="/admin">
                <Button data-testid="nav-admin-btn" className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm h-9 px-3 md:px-4 text-xs md:text-sm">
                  {t("nav.admin")}
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button data-testid="nav-login-btn" variant="outline" className="bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange font-heading uppercase tracking-widest rounded-sm h-9 px-3 md:px-4 text-xs md:text-sm">
                  <span className="hidden sm:inline">{t("nav.operator_login")}</span><span className="sm:hidden">{t("nav.login")}</span>
                </Button>
              </Link>
            )}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-sm border border-navy-border text-white hover:text-orange hover:border-orange"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Open menu"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-navy-border bg-navy-deep" data-testid="nav-mobile-menu">
            <NavLink to="/" end className={mobileNavLink} onClick={() => setMobileOpen(false)} data-testid="mobile-nav-home">{t("nav.home")}</NavLink>
            <NavLink to="/games" className={mobileNavLink} onClick={() => setMobileOpen(false)} data-testid="mobile-nav-games">{t("nav.games")}</NavLink>
            <NavLink to="/teams" className={mobileNavLink} onClick={() => setMobileOpen(false)} data-testid="mobile-nav-teams">{t("nav.teams")}</NavLink>
            <NavLink to="/players" className={mobileNavLink} onClick={() => setMobileOpen(false)} data-testid="mobile-nav-players">{t("nav.players")}</NavLink>
            <NavLink to="/leaders" className={mobileNavLink} onClick={() => setMobileOpen(false)} data-testid="mobile-nav-leaders">{t("nav.leaders")}</NavLink>
            <div className="px-4 py-3 border-t border-navy-border flex items-center gap-2">
              <Globe size={14} className="text-orange" />
              <LanguageToggle variant="mobile" />
            </div>
          </div>
        )}
      </header>
      <main>
        <Outlet />
      </main>
      <footer className="mt-16 md:mt-24 border-t border-navy-border bg-navy-deep">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10 grid md:grid-cols-3 gap-6 md:gap-8">
          <div>
            <GpsLogo />
            <p className="text-ink-muted text-sm mt-3 max-w-xs">
              {settings?.footer_tagline || t("footer.default_tagline")}
            </p>
          </div>
          <div>
            <h4 className="font-heading uppercase tracking-widest text-orange text-sm mb-3">{t("footer.explore")}</h4>
            <ul className="text-ink-secondary text-sm space-y-1">
              <li><Link to="/games" className="hover:text-white">{t("nav.games")}</Link></li>
              <li><Link to="/teams" className="hover:text-white">{t("nav.teams")}</Link></li>
              <li><Link to="/players" className="hover:text-white">{t("nav.players")}</Link></li>
              <li><Link to="/leaders" className="hover:text-white">{t("nav.leaders")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading uppercase tracking-widest text-orange text-sm mb-3">{t("footer.operator_col")}</h4>
            <ul className="text-ink-secondary text-sm space-y-1">
              <li><Link to="/login" className="hover:text-white">{t("nav.login")}</Link></li>
              <li><Link to="/admin" className="hover:text-white">{t("footer.admin_panel")}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-navy-border py-4 text-center text-ink-muted text-xs font-mono tracking-widest">
          {t("footer.built_for")}
        </div>
      </footer>
    </div>
  );
}

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const item = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-sm font-heading uppercase tracking-widest text-sm transition-all ${
      isActive
        ? "bg-orange text-white"
        : "text-ink-secondary hover:text-white hover:bg-navy-surface"
    }`;

  const sidebarContent = (
    <>
      <div className="p-5 border-b border-navy-border flex items-center justify-between">
        <GpsLogo />
        <button
          type="button"
          className="lg:hidden text-ink-secondary hover:text-white"
          onClick={() => setSidebarOpen(false)}
          data-testid="admin-sidebar-close"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {isAdmin && (
          <NavLink to="/admin" end className={item} onClick={() => setSidebarOpen(false)} data-testid="admin-nav-dashboard">
            <LayoutDashboard size={18} /> Dashboard
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/admin/tournaments" className={item} onClick={() => setSidebarOpen(false)} data-testid="admin-nav-tournaments">
            <Trophy size={18} /> Tournaments
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/admin/teams" className={item} onClick={() => setSidebarOpen(false)} data-testid="admin-nav-teams">
            <Users size={18} /> Teams
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/admin/players" className={item} onClick={() => setSidebarOpen(false)} data-testid="admin-nav-players">
            <UserSquare2 size={18} /> Players
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/admin/venues" className={item} onClick={() => setSidebarOpen(false)} data-testid="admin-nav-venues">
            <MapPin size={18} /> Venues
          </NavLink>
        )}
        <NavLink to="/admin/games" className={item} onClick={() => setSidebarOpen(false)} data-testid="admin-nav-games">
          <CalendarDays size={18} /> Games
        </NavLink>
        {isAdmin && (
          <NavLink to="/admin/users" className={item} onClick={() => setSidebarOpen(false)} data-testid="admin-nav-users">
            <UserCog size={18} /> Users
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/admin/settings" className={item} onClick={() => setSidebarOpen(false)} data-testid="admin-nav-settings">
            <Settings size={18} /> Site Settings
          </NavLink>
        )}
      </nav>
      <div className="p-3 border-t border-navy-border space-y-2">
        <Link to="/" className="flex items-center gap-2 text-ink-secondary hover:text-orange text-xs uppercase tracking-widest px-2">
          <Activity size={14} /> Public Portal
        </Link>
        <div className="px-2 text-xs text-ink-muted truncate">
          {user?.email}
          <span className="ml-2 text-orange font-heading uppercase tracking-widest">{user?.role}</span>
        </div>
        <Button
          data-testid="admin-logout-btn"
          onClick={async () => { await logout(); navigate("/login"); }}
          variant="outline"
          className="w-full bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange font-heading uppercase tracking-widest rounded-sm"
        >
          <LogOut size={14} className="mr-2" /> Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-navy text-ink-primary lg:flex">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 bg-navy-deep border-b border-navy-border px-3 py-2 flex items-center justify-between">
        <GpsLogo />
        <button
          type="button"
          className="inline-flex items-center justify-center w-10 h-10 rounded-sm border border-navy-border text-white hover:text-orange hover:border-orange"
          onClick={() => setSidebarOpen(true)}
          data-testid="admin-sidebar-open"
          aria-label="Open menu"
        >
          <Menu size={18} />
        </button>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-navy-deep border-r border-navy-border flex-col shrink-0">
        {sidebarContent}
      </aside>

      {/* Mobile drawer */}
      {sidebarOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/60 z-40"
            onClick={() => setSidebarOpen(false)}
            data-testid="admin-sidebar-backdrop"
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-navy-deep border-r border-navy-border z-50 flex flex-col" data-testid="admin-sidebar-drawer">
            {sidebarContent}
          </aside>
        </>
      )}

      <main className="flex-1 overflow-x-hidden min-w-0">
        <Outlet />
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, action, testid }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8" data-testid={testid}>
      <div>
        <h1 className="font-heading font-black uppercase tracking-tight text-4xl md:text-5xl text-white">{title}</h1>
        {subtitle && <p className="text-ink-secondary mt-1 text-sm md:text-base">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
